import React, { Fragment, FunctionComponent, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Stack,
    TextField,
    Typography,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { DatePicker, Select } from '@starjack/shared-components';
import {
    useGetLiftOperatorQuery,
    useLazyGetProductDetailsQuery,
} from '@starjack/entities/liftoperator';
import { useAuthentication } from '@starjack/features/authentication';
import {
    addTicketHolderToTicket,
    createSubmitOrderIdentificationFromTicketCollection,
    createSubmitOrderTicketsFromTicketCollection,
    getPriceTotalFromTicketCollection,
    OrderDTO,
    OrderTicket,
    OrderTicketCollection,
    removeTicketHolder,
    SubmitOrderTicket,
    TicketHolder,
    TicketIdentification,
} from '@starjack/entities/orderTicket';
import { TicketHolderSelection } from '@starjack/entities/orderTicket';
import { LoadingButton } from '@mui/lab';
import { useNotification } from '@starjack/features/notifications';
import { SuccessDialog } from '@starjack/entities/orderTicket';
import { useTranslation } from 'react-i18next';
import { useOrderTicketValidation } from '@starjack/entities/orderTicket';
import { XlsxImportDialog } from '@starjack/entities/orderTicket';
import {
    ValidatedImportRow,
    processDataImport,
} from '@starjack/entities/orderTicket';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import {
    CloudArrowUpIcon,
    ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { v4 } from 'uuid';
import { isValid } from 'date-fns';
import { Money } from '@starjack/entities/money';

type QuantityCounterProps = {
    initial?: number;
    value?: number;
    min?: number;
    onCounterChange: (next: number, action?: 'add' | 'subtract') => void;
};

type UsePaginationReturn<T> = {
    currentItems: T[];
    setNextPage: (page: number) => void;
    page: number;
    setItemsPerPage: (itemsPerPage: number) => void;
    itemsPerPage: number;
};

type OrderTicketsContentProps = {
    QuantityCounter: FunctionComponent<QuantityCounterProps>;
    useParseFileData: () => (file: File) => Promise<unknown[]>;
    useValidateImportData: () => (
        data: unknown[],
        availableTickets: OrderTicketCollection[]
    ) => ValidatedImportRow[];
    usePagination: <T>(params: {
        items: T[];
        itemsPerPage?: number;
    }) => UsePaginationReturn<T>;
    columnMapping: Record<string, string>;
    postOrder: (
        body: OrderDTO
    ) => Promise<
        [{ orderId: string; status: string; location: string } | null, unknown]
    >;
    postPayOrder: (body: {
        orderId: string;
        token: string | null;
        saveProfile: boolean;
        successUrl: string;
        errorUrl: string;
    }) => Promise<
        [{ orderId: string; status: string; location: string } | null, unknown]
    >;
    getMoneyAsString: (money: Money) => string;
    confirmationNumberFormat?: 'admin' | 'b2b';
    showStartDayInTicketHolder?: boolean;
};

export const OrderTicketsContent: FunctionComponent<
    OrderTicketsContentProps
> = ({
    QuantityCounter,
    useParseFileData,
    useValidateImportData,
    usePagination,
    columnMapping,
    postOrder,
    postPayOrder,
    getMoneyAsString,
    confirmationNumberFormat = 'admin',
    showStartDayInTicketHolder = false,
}) => {
    const [datePickerValue, setDatePickerValue] = useState<Date | null>(null);
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { data: liftOperator } = useGetLiftOperatorQuery({ liftOperatorId });
    const [getProductDetails] = useLazyGetProductDetailsQuery();
    const [ticketCollectionList, setTicketCollectionList] = useState<
        OrderTicketCollection[]
    >([]);
    const [selectedTicketCollection, setSelectedTicketCollection] = useState<
        OrderTicketCollection[]
    >([]);

    const [loading, setLoading] = useState(false);
    const [canOrder, setCanOrder] = useState(false);
    const [referenceNumber, setReferenceNumber] = useState('');
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showXlsxImport, setShowXlsxImport] = useState(false);
    const [useSharedConfirmationNumber, setUseSharedConfirmationNumber] =
        useState(false);
    const { addNotification } = useNotification();
    const { t, i18n } = useTranslation();
    const [showErrors, setShowErrors] = useState(false);
    const { validationErrorMap, hasErrors } = useOrderTicketValidation(
        selectedTicketCollection
    );

    const onSelectChange = (event: React.ChangeEvent<{ value: string }>) => {
        const value = parseInt(event.target.value);

        if (isNaN(value)) return;

        const ticketCollection = ticketCollectionList[value];

        if (ticketCollection) {
            setSelectedTicketCollection((prevState) => [
                ticketCollection,
                ...prevState,
            ]);

            setTicketCollectionList((prevState) =>
                prevState.filter((_, index) => index !== value)
            );
        }
    };

    const onDelete = (ticketCollection: OrderTicketCollection) => {
        setShowErrors(false);
        setSelectedTicketCollection((prevState) =>
            prevState.filter((selected) => selected.id !== ticketCollection.id)
        );
        setTicketCollectionList((prevState) => [
            ticketCollection,
            ...prevState,
        ]);
    };

    const onAddTicketHolder = (
        ticketCollection: OrderTicketCollection,
        ticketHolder: TicketHolder
    ) => {
        setShowErrors(false);
        const selectedCopy: OrderTicketCollection[] = [
            ...selectedTicketCollection,
        ];

        const collectionIndex = selectedCopy.findIndex(
            (c) => c.id === ticketCollection.id
        );

        if (collectionIndex > -1) {
            selectedCopy[collectionIndex] = addTicketHolderToTicket({
                ticketCollection,
                ticketHolder,
            });
        }

        setSelectedTicketCollection(selectedCopy);
    };

    const onRemoveTicketHolder = (
        ticketCollection: OrderTicketCollection,
        ticketHolder: TicketHolder
    ) => {
        setShowErrors(false);
        const selectedCopy: OrderTicketCollection[] = [
            ...selectedTicketCollection,
        ];

        const collectionIndex = selectedCopy.findIndex(
            (c) => c.id === ticketCollection.id
        );

        if (collectionIndex > -1) {
            selectedCopy[collectionIndex] = removeTicketHolder({
                ticketCollection,
                ticketHolder,
            });
        }

        setSelectedTicketCollection(selectedCopy);
    };

    const onUpdateTicketHolder = (
        ticketCollection: OrderTicketCollection,
        ticketHolderToUpdate: TicketHolder
    ) => {
        const selectedCopy: OrderTicketCollection[] = [
            ...selectedTicketCollection,
        ];

        const collectionIndex = selectedCopy.findIndex(
            (c) => c.id === ticketCollection.id
        );

        if (collectionIndex > -1) {
            const ticketHolders = [
                ...ticketCollection.ticketHoldersMap[
                    ticketHolderToUpdate.ticketId
                ],
            ];

            const ticketHolderIndex = ticketHolders.findIndex(
                (t) => t.id === ticketHolderToUpdate.id
            );

            if (ticketHolderIndex > -1) {
                ticketHolders[ticketHolderIndex] = ticketHolderToUpdate;

                selectedCopy[collectionIndex] = {
                    ...ticketCollection,
                    ticketHoldersMap: {
                        ...ticketCollection.ticketHoldersMap,
                        [ticketHolderToUpdate.ticketId]: ticketHolders,
                    },
                };
            }
        }

        setSelectedTicketCollection(selectedCopy);
    };

    const orderTickets = async () => {
        setLoading(true);

        if (hasErrors) {
            setLoading(false);
            setShowErrors(true);
            return;
        }

        setShowErrors(false);

        let submitTickets: SubmitOrderTicket[] = [];
        let ticketIdentification: TicketIdentification[] = [];

        const confirmationNumberToUse = useSharedConfirmationNumber
            ? confirmationNumberFormat === 'admin'
                ? `SJ-${v4().replace(/-/g, '')}`.substring(0, 32)
                : `SJ${v4().replace(/-/g, '')}`.substring(0, 12)
            : '';

        selectedTicketCollection.forEach((ticketCollection) => {
            const collectionTickets =
                createSubmitOrderTicketsFromTicketCollection(
                    ticketCollection,
                    liftOperator!,
                    confirmationNumberToUse
                );
            submitTickets = [...submitTickets, ...collectionTickets];

            const collectionIdentifications =
                createSubmitOrderIdentificationFromTicketCollection(
                    ticketCollection,
                    liftOperator!
                );
            ticketIdentification = [
                ...ticketIdentification,
                ...collectionIdentifications,
            ];
        });

        const orderDTO: OrderDTO = {
            tickets: submitTickets,
            identifications: ticketIdentification,
            vouchers: [],
            address: {
                firstname: liftOperatorId,
                lastname: t('ticketOrder.address.b2bUser'),
                ...liftOperator!.address,
            },
            invoiceAddress: null,
            shippingCosts: {
                amount: 0,
                currency: 'EUR',
            },
            profile: '',
            paymentMethod: 'TOKEN',
            psp: 'SIX',
            environment: 'B2B',
            shopProducts: [],
            reference: referenceNumber || undefined,
        };

        const [order, error] = await postOrder(orderDTO);

        if (error) {
            console.error(error);
            addNotification({
                message: t('ticketOrder.errors.orderError'),
                type: 'error',
            });

            setLoading(false);
            return;
        }

        const [, postPayOrderError] = await postPayOrder({
            orderId: order!.orderId,
            token: null,
            saveProfile: true,
            successUrl: `${window.location.href}?order-success=true`,
            errorUrl: `${window.location.href}?order-success=false`,
        });

        if (postPayOrderError) {
            addNotification({
                type: 'error',
                message: t('ticketOrder.errors.orderError'),
            });
            setLoading(false);
            return;
        }

        setReferenceNumber('');
        setLoading(false);
        setShowSuccessDialog(true);
    };

    const getPriceTotal = () => {
        return selectedTicketCollection.reduce(
            (total, ticketCollection) => {
                return {
                    amount:
                        total.amount +
                        getPriceTotalFromTicketCollection(ticketCollection)
                            .amount,
                    currency: 'EUR',
                };
            },
            { amount: 0, currency: 'EUR' }
        );
    };

    const handleXlsxImport = (importData: ValidatedImportRow[]) => {
        try {
            if (!datePickerValue || !isValid(datePickerValue)) {
                throw new Error('Invalid selected date');
            }

            const importedCollections = processDataImport({
                importData,
                availableTicketCollections: [
                    ...ticketCollectionList,
                    ...selectedTicketCollection,
                ],
                currentDate: datePickerValue,
            });

            const newSelectedCollections = [...selectedTicketCollection];

            importedCollections.forEach((importedCollection) => {
                const existingIndex = newSelectedCollections.findIndex(
                    (existing) => existing.id === importedCollection.id
                );

                if (existingIndex >= 0) {
                    newSelectedCollections[existingIndex] = importedCollection;
                } else {
                    newSelectedCollections.push(importedCollection);

                    setTicketCollectionList((prev) =>
                        prev.filter(
                            (available) =>
                                available.id !== importedCollection.id
                        )
                    );
                }
            });

            setSelectedTicketCollection(newSelectedCollections);

            addNotification({
                type: 'success',
                message: t(
                    'ticketOrder.xlsxImport.successMessage',
                    `Successfully imported ${importData.length} ticket holders`
                ),
            });
        } catch (error) {
            addNotification({
                type: 'error',
                message: t(
                    'ticketOrder.xlsxImport.errorMessage',
                    `Import failed: ${(error as Error).message}`
                ),
            });
        }
    };

    useEffect(() => {
        if (datePickerValue && liftOperatorId) {
            getProductDetails({
                liftOperatorId,
                startDay: datePickerValue,
            })
                .unwrap()
                .then((collections: OrderTicketCollection[]) => {
                    const filteredCollections = collections.filter(
                        (collection) => {
                            const tickets: OrderTicket[] = Object.values(
                                collection.ticketMap
                            );
                            return tickets.some((t) => !t.photoRequired);
                        }
                    );

                    const processedCollections = filteredCollections.map(
                        (collection) => {
                            const tickets: OrderTicket[] = Object.values(
                                collection.ticketMap
                            ).filter((t) => !t.photoRequired);

                            if (tickets.length === 0) {
                                return null;
                            }

                            const newTicketMap: Record<string, OrderTicket> =
                                {};
                            const newTicketHoldersMap: Record<
                                string,
                                TicketHolder[]
                            > = {};

                            tickets.forEach((ticket) => {
                                newTicketMap[ticket.productId] = ticket;
                                newTicketHoldersMap[ticket.productId] =
                                    collection.ticketHoldersMap[
                                        ticket.productId
                                    ] || [];
                            });

                            return {
                                ...collection,
                                ticketMap: newTicketMap,
                                ticketHoldersMap: newTicketHoldersMap,
                            };
                        }
                    );

                    setTicketCollectionList(
                        processedCollections.filter(
                            (c): c is OrderTicketCollection => c !== null
                        )
                    );
                });
        }
    }, [datePickerValue, liftOperatorId, getProductDetails]);

    const hasTicketholders = selectedTicketCollection.some(
        (tC) => tC.numberOfTicketHolders > 0
    );

    if (canOrder !== hasTicketholders) {
        setCanOrder(hasTicketholders);
    }

    const priceTotal = getPriceTotal();

    return (
        <Fragment>
            <Typography
                sx={{
                    mb: { xs: 4, md: 8 },
                }}
                variant='h4'
            >
                {t('ticketOrder.title')}
            </Typography>
            <Stack
                alignItems='flex-start'
                spacing={{ xs: 3, md: 6 }}
                sx={{
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        width: { xs: '100%', sm: 400 },
                    }}
                >
                    <DatePicker
                        value={datePickerValue}
                        onChange={setDatePickerValue}
                        label={t('ticketOrder.validityDate')}
                        disablePast
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: 'flex-start',
                        mb: 2,
                        width: '100%',
                        gap: { xs: 2, md: 0 },
                    }}
                >
                    <Box sx={{ width: { xs: '100%', sm: 400 } }}>
                        <Box>
                            <Select
                                sx={{
                                    width: { xs: '100%', sm: 400 },
                                }}
                                label={t('ticketOrder.ticketSelection')}
                                value='-1'
                                onChange={onSelectChange}
                                disabled={
                                    ticketCollectionList.length === 0 ||
                                    !liftOperator
                                }
                            >
                                <option value='-1'>
                                    {t('ticketOrder.addAnotherTicket')}
                                </option>

                                {ticketCollectionList.map(
                                    (ticketCollection, index) => (
                                        <option key={index} value={index}>
                                            {ticketCollection.name[
                                                i18n.language
                                            ] || ticketCollection.name.de}
                                        </option>
                                    )
                                )}
                            </Select>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: { xs: 'stretch', md: 'flex-end' },
                            ml: { xs: 0, md: 'auto' },
                            width: { xs: '100%', md: 'auto' },
                            minWidth: { md: 280 },
                        }}
                    >
                        <TextField
                            sx={{
                                width: '100%',
                                mb: 2,
                            }}
                            value={referenceNumber || ''}
                            onChange={(e) => {
                                const { value } = e.target;
                                setReferenceNumber(value);
                            }}
                            label={t('ticketOrder.bookingReference')}
                        />

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'stretch', sm: 'center' },
                                gap: 2,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                }}
                            >
                                {t('ticketOrder.totalPrice')}:{' '}
                                {getMoneyAsString(priceTotal)}
                            </Typography>

                            <LoadingButton
                                sx={{
                                    width: { xs: '100%', sm: 200 },
                                }}
                                variant='contained'
                                onClick={orderTickets}
                                disabled={!canOrder}
                                loading={loading}
                                startIcon={<ShoppingCartIcon />}
                            >
                                {t('ticketOrder.order')}
                            </LoadingButton>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                    <Button
                        variant='outlined'
                        size='small'
                        startIcon={
                            <CloudArrowUpIcon
                                style={{ width: 16, height: 16 }}
                            />
                        }
                        onClick={() => setShowXlsxImport(true)}
                        disabled={!datePickerValue}
                    >
                        {t('ticketOrder.xlsxImport.importData')}
                    </Button>

                    <Button
                        variant='outlined'
                        size='small'
                        startIcon={
                            <ArrowDownTrayIcon
                                style={{ width: 16, height: 16 }}
                            />
                        }
                        onClick={() => {
                            const link = document.createElement('a');
                            link.href =
                                '/mass-import/mass-import-template.xlsx';
                            link.download = 'import-customers-template.xlsx';
                            link.click();
                        }}
                    >
                        {t('ticketOrder.xlsxImport.downloadTemplate')}
                    </Button>
                </Box>

                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={useSharedConfirmationNumber}
                                onChange={(e) =>
                                    setUseSharedConfirmationNumber(
                                        e.target.checked
                                    )
                                }
                            />
                        }
                        label={t('ticketOrder.sharedConfirmationNumber')}
                        sx={{ mt: 1 }}
                    />
                    <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ ml: { xs: 0, sm: 4 }, display: 'block', mt: 0.5 }}
                    >
                        {t('ticketOrder.sharedConfirmationCaption')}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        mt: { xs: 3, md: 6 },
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        width: '100%',
                    }}
                >
                    {selectedTicketCollection.map((selected, index) => (
                        <TicketHolderSelection
                            key={index}
                            ticketCollection={selected}
                            onDelete={onDelete}
                            onAddTicketHolder={onAddTicketHolder}
                            onRemoveTicketHolder={onRemoveTicketHolder}
                            onTicketHolderUpdate={onUpdateTicketHolder}
                            validationErrors={
                                showErrors ? validationErrorMap : null
                            }
                            QuantityCounter={QuantityCounter}
                            showStartDay={showStartDayInTicketHolder}
                        />
                    ))}
                </Box>
            </Stack>

            <SuccessDialog
                open={showSuccessDialog}
                onClose={() => {
                    setSelectedTicketCollection([]);
                    setShowSuccessDialog(false);
                }}
            />

            <XlsxImportDialog
                open={showXlsxImport}
                onClose={() => setShowXlsxImport(false)}
                onImport={handleXlsxImport}
                availableTickets={[
                    ...ticketCollectionList,
                    ...selectedTicketCollection,
                ]}
                useParseFileData={useParseFileData}
                useValidateImportData={useValidateImportData}
                usePagination={usePagination}
                columnMapping={columnMapping}
            />
        </Fragment>
    );
};
